import type React from 'react';
import { createContext, useContext, useState, useMemo, useCallback } from 'react';
import type { GroupConfig } from 'types/notifications';
import { SocRole } from 'types/notifications';
import { AppRole } from 'types/auth';

// ─── Fallback Config from Environment ───

const SOC_OPERATORS_GROUP_ID = import.meta.env.REACT_APP_SOC_OPERATORS_GROUP_ID || '';
const SOC_ADMIN_GROUP_ID = import.meta.env.REACT_APP_SOC_ADMIN_GROUP_ID || '';

/**
 * Environment-based fallback group configs.
 * Used when settings are not yet loaded or empty.
 */
function getEnvFallbackGroupConfigs(): GroupConfig[] {
  const configs: GroupConfig[] = [];

  if (SOC_OPERATORS_GROUP_ID) {
    configs.push({
      groupId: SOC_OPERATORS_GROUP_ID,
      displayName: 'SOC Operators (env)',
      assignedRoles: [SocRole.Operator],
      cachedMemberCount: 0,
      cachedMemberCountTime: null,
    });
  }

  if (SOC_ADMIN_GROUP_ID) {
    // Check if same as operators - if so, add Admin role to same config
    const operatorConfig = configs.find((c) => c.groupId === SOC_ADMIN_GROUP_ID);
    if (operatorConfig) {
      operatorConfig.assignedRoles.push(SocRole.Admin);
    } else {
      configs.push({
        groupId: SOC_ADMIN_GROUP_ID,
        displayName: 'SOC Admins (env)',
        assignedRoles: [SocRole.Admin],
        cachedMemberCount: 0,
        cachedMemberCountTime: null,
      });
    }
  }

  return configs;
}

// ─── Context ───

interface SecurityGroupConfigContextValue {
  /** Current group configurations */
  groupConfigs: GroupConfig[];
  /** Whether settings have been loaded */
  isLoaded: boolean;
  /** Map user's group IDs to app roles */
  mapGroupsToAppRoles: (userGroupIds: string[]) => AppRole[];
  /** Check if user has app access based on group membership */
  hasAppAccess: (userGroupIds: string[]) => boolean;
  /** Update configs from settings */
  updateFromSettings: (settingsGroupConfigs: GroupConfig[]) => void;
}

const SecurityGroupConfigContext = createContext<SecurityGroupConfigContextValue | null>(null);

// ─── Provider ───

interface SecurityGroupConfigProviderProps {
  children: React.ReactNode;
}

/**
 * Provider for security group configuration.
 * Starts with env var fallback, updates when settings are loaded.
 */
export const SecurityGroupConfigProvider: React.FC<SecurityGroupConfigProviderProps> = ({ children }) => {
  const [groupConfigs, setGroupConfigs] = useState<GroupConfig[]>(getEnvFallbackGroupConfigs);
  const [isLoaded, setIsLoaded] = useState(false);

  const updateFromSettings = useCallback((settingsGroupConfigs: GroupConfig[]) => {
    if (settingsGroupConfigs.length > 0) {
      // Use settings as source of truth
      setGroupConfigs(settingsGroupConfigs);
    } else {
      // Keep env fallback if settings are empty
      setGroupConfigs(getEnvFallbackGroupConfigs());
    }
    setIsLoaded(true);
  }, []);

  const mapGroupsToAppRoles = useCallback(
    (userGroupIds: string[]): AppRole[] => {
      const roles: AppRole[] = [];

      for (const config of groupConfigs) {
        if (userGroupIds.includes(config.groupId)) {
          for (const socRole of config.assignedRoles) {
            if (socRole === SocRole.Admin && !roles.includes(AppRole.Admin)) {
              roles.push(AppRole.Admin);
            }
            if (socRole === SocRole.Operator && !roles.includes(AppRole.Operator)) {
              roles.push(AppRole.Operator);
            }
          }
        }
      }

      return roles;
    },
    [groupConfigs]
  );

  const hasAppAccess = useCallback(
    (userGroupIds: string[]): boolean => {
      return groupConfigs.some((config) => userGroupIds.includes(config.groupId));
    },
    [groupConfigs]
  );

  const value = useMemo<SecurityGroupConfigContextValue>(
    () => ({
      groupConfigs,
      isLoaded,
      mapGroupsToAppRoles,
      hasAppAccess,
      updateFromSettings,
    }),
    [groupConfigs, isLoaded, mapGroupsToAppRoles, hasAppAccess, updateFromSettings]
  );

  return (
    <SecurityGroupConfigContext.Provider value={value}>
      {children}
    </SecurityGroupConfigContext.Provider>
  );
};

// ─── Hook ───

export function useSecurityGroupConfig(): SecurityGroupConfigContextValue {
  const context = useContext(SecurityGroupConfigContext);
  if (!context) {
    throw new Error('useSecurityGroupConfig must be used within SecurityGroupConfigProvider');
  }
  return context;
}
